// Pure i18n core for the whole platform — the admin app (keys under `admin.*`)
// and the public spectator view share one catalogue per language. Framework-free
// (no React) so the lookup/interpolation/pluralisation logic can be unit-tested
// directly and reused anywhere. The per-language message catalogues live in
// `locales/*.json` — flat-ish, nested JSON files that can be handed to a
// translator as-is. English is the source of truth for the message shape; every
// other locale is validated structurally against it and falls back to English
// for any missing key.
//
// The set of supported languages is owned by the domain layer
// (`src/domain/languages.ts`) so the server can validate a company's
// `defaultLanguage` without importing any catalogue.

import type { LanguageCode } from '../domain/languages';
import { toLanguageCode } from '../domain/languages';
import en from './locales/en.json';
import zh from './locales/zh.json';
import ru from './locales/ru.json';
import km from './locales/km.json';

export type Locale = LanguageCode;

export interface LocaleMeta {
  /** BCP-47 code used for `Intl` date/number formatting. */
  code: string;
  /** Endonym — the language's own name, shown in the language selector. */
  label: string;
  /** Text direction. */
  dir: 'ltr' | 'rtl';
}

export const DEFAULT_LOCALE: Locale = 'en';

// English is the canonical shape; other catalogues are cast to it and any gaps
// resolve to English at runtime (see `translate`).
export type Messages = typeof en;

export const LOCALE_META: Record<Locale, LocaleMeta> = {
  en: { code: 'en', label: 'English', dir: 'ltr' },
  zh: { code: 'zh', label: '中文', dir: 'ltr' },
  ru: { code: 'ru', label: 'Русский', dir: 'ltr' },
  km: { code: 'km', label: 'ខ្មែរ', dir: 'ltr' },
};

const MESSAGES: Record<Locale, Messages> = {
  en,
  zh: zh as Messages,
  ru: ru as Messages,
  km: km as Messages,
};

/** Every available locale, in display order. */
export const LOCALES = Object.keys(LOCALE_META) as Locale[];

/**
 * Narrow an arbitrary string to a supported `Locale`, or `null`. A full BCP-47
 * tag (e.g. a device's `zh-Hans-CN`) matches by its primary subtag.
 */
export function toLocale(value: string | null | undefined): Locale | null {
  return toLanguageCode(value);
}

export type TranslateParams = Record<string, string | number>;

/** Resolve a dotted key path (e.g. `groups.completed`) to its string value. */
function lookup(messages: Messages, key: string): string | undefined {
  let current: unknown = messages;
  for (const part of key.split('.')) {
    if (typeof current !== 'object' || current === null) return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return typeof current === 'string' ? current : undefined;
}

/** Substitute `{name}` placeholders with their param values. */
function interpolate(template: string, params?: TranslateParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = params[name];
    return value === undefined ? match : String(value);
  });
}

/**
 * Translate `key` into `locale`, interpolating `params`. When `params.count` is
 * a number, the CLDR plural category for the locale selects a `key_one` /
 * `key_other` variant (falling back to the bare key). Missing keys fall back to
 * the English catalogue, then to the key itself so a gap is visible, never blank.
 */
export function translate(
  locale: Locale,
  key: string,
  params?: TranslateParams,
): string {
  const select = (k: string): string | undefined =>
    lookup(MESSAGES[locale], k) ?? lookup(MESSAGES[DEFAULT_LOCALE], k);

  let template: string | undefined;
  if (params && typeof params.count === 'number') {
    const category = new Intl.PluralRules(LOCALE_META[locale].code).select(
      params.count,
    );
    template = select(`${key}_${category}`) ?? select(`${key}_other`);
  }
  template ??= select(key);

  return template === undefined ? key : interpolate(template, params);
}
